import { T } from '../reportTokens';
import { getCurrencySymbol, localizeCurrencyText, useCurrencyRevision } from '../../../utils/currency';

export type OfferType = '% Off' | '$ Off' | 'BOGO' | 'Free Item' | 'Free Delivery';
export type OfferStatus = 'Active' | 'Paused' | 'Expired' | 'Draft';
export type OrderType = 'dine_in' | 'takeout' | 'delivery' | 'online';

export type Offer = {
    id: string;
    name: string;
    type: OfferType;
    status: OfferStatus;
    discountValue: string;
    applyMode: 'Auto-Apply' | 'Manual' | 'Promo Code';
    stacking: 'Exclusive' | 'Stackable';
    appLevel: 'Order' | 'Item';
    startDate: string;
    endDate: string | null;
    redemptions: number;
    discountGiven: number;
    aov: number;
    activatedAt: string;
};

export const OFFERS: Offer[] = [
    { id: 'o1', name: '20% Off Weekday Lunch', type: '% Off', status: 'Active', discountValue: '20%', applyMode: 'Auto-Apply', stacking: 'Exclusive', appLevel: 'Order', startDate: '2026-04-01', endDate: '2026-06-30', redemptions: 327, discountGiven: 1842.30, aov: 41.20, activatedAt: '2026-04-01' },
    { id: 'o2', name: 'BOGO Burger Tuesday', type: 'BOGO', status: 'Active', discountValue: 'Buy 1 Get 1', applyMode: 'Auto-Apply', stacking: 'Exclusive', appLevel: 'Item', startDate: '2026-03-15', endDate: null, redemptions: 248, discountGiven: 1196.00, aov: 36.40, activatedAt: '2026-03-15' },
    { id: 'o3', name: '$5 Off Orders Over $30', type: '$ Off', status: 'Active', discountValue: '$5.00', applyMode: 'Promo Code', stacking: 'Stackable', appLevel: 'Order', startDate: '2026-04-15', endDate: '2026-05-30', redemptions: 187, discountGiven: 935.00, aov: 38.90, activatedAt: '2026-04-15' },
    { id: 'o4', name: 'Free Delivery Friday', type: 'Free Delivery', status: 'Active', discountValue: 'Free Shipping', applyMode: 'Auto-Apply', stacking: 'Stackable', appLevel: 'Order', startDate: '2026-04-01', endDate: null, redemptions: 156, discountGiven: 624.00, aov: 32.10, activatedAt: '2026-04-01' },
    { id: 'o5', name: 'Free Dessert with Entree', type: 'Free Item', status: 'Active', discountValue: 'Free Dessert', applyMode: 'Auto-Apply', stacking: 'Exclusive', appLevel: 'Order', startDate: '2026-05-01', endDate: '2026-06-15', redemptions: 132, discountGiven: 924.00, aov: 47.80, activatedAt: '2026-05-01' },
    { id: 'o6', name: 'Student Discount 15%', type: '% Off', status: 'Active', discountValue: '15%', applyMode: 'Promo Code', stacking: 'Exclusive', appLevel: 'Order', startDate: '2026-02-01', endDate: null, redemptions: 95, discountGiven: 410.25, aov: 28.80, activatedAt: '2026-02-01' },
    { id: 'o7', name: '$10 Off First Order', type: '$ Off', status: 'Active', discountValue: '$10.00', applyMode: 'Promo Code', stacking: 'Exclusive', appLevel: 'Order', startDate: '2026-01-01', endDate: null, redemptions: 73, discountGiven: 730.00, aov: 44.50, activatedAt: '2026-01-01' },
    { id: 'o8', name: 'Happy Hour 25% Off', type: '% Off', status: 'Paused', discountValue: '25%', applyMode: 'Auto-Apply', stacking: 'Exclusive', appLevel: 'Item', startDate: '2026-03-01', endDate: '2026-05-31', redemptions: 29, discountGiven: 188.50, aov: 22.10, activatedAt: '2026-03-01' },
    { id: 'o9', name: 'Weekend Brunch Special', type: '$ Off', status: 'Expired', discountValue: '$3.00', applyMode: 'Auto-Apply', stacking: 'Exclusive', appLevel: 'Order', startDate: '2026-02-15', endDate: '2026-04-15', redemptions: 0, discountGiven: 0, aov: 0, activatedAt: '2026-02-15' },
];

export const PRIOR_PERIOD = { redemptions: 1019, discount: 4830.25, aov: 37.55 };

export const REDEMPTION_TREND: Array<{ date: string; count: number; dine_in: number; takeout: number; delivery: number; online: number }> = (() => {
    const days = [];
    for (let i = 29; i >= 0; i--) {
        const d = new Date('2026-05-22T00:00:00Z');
        d.setDate(d.getDate() - i);
        const date = d.toISOString().slice(0, 10);
        const base = 30 + Math.floor(Math.sin(i / 4) * 12) + ((i * 7) % 18);
        const dine_in = Math.floor(base * 0.32);
        const takeout = Math.floor(base * 0.24);
        const delivery = Math.floor(base * 0.22);
        const online = base - dine_in - takeout - delivery;
        days.push({ date, count: base, dine_in, takeout, delivery, online });
    }
    return days;
})();

export const TOP_ITEMS_BY_OFFER: Record<string, Array<{ name: string; count: number }>> = {
    o1: [{ name: 'Caesar Salad', count: 142 }, { name: 'Grilled Chicken Wrap', count: 118 }, { name: 'Veggie Burger', count: 87 }, { name: 'Tomato Soup', count: 64 }, { name: 'Iced Tea', count: 51 }],
    o2: [{ name: 'Classic Burger', count: 248 }, { name: 'BBQ Burger', count: 165 }, { name: 'Cheese Burger', count: 142 }, { name: 'Side Fries', count: 198 }, { name: 'Soda', count: 211 }],
    o3: [{ name: 'Pepperoni Pizza', count: 92 }, { name: 'Margherita Pizza', count: 78 }, { name: 'Pasta Carbonara', count: 51 }, { name: 'Garlic Bread', count: 67 }],
    o5: [{ name: 'Steak Frites', count: 84 }, { name: 'Salmon Fillet', count: 47 }, { name: 'Tiramisu', count: 132 }, { name: 'Chocolate Lava Cake', count: 119 }],
};

export const PROMO_CODES_BY_OFFER: Record<string, Array<{ code: string; uses: number; remaining: number | null; revenue: number; lastUsed: string | null }>> = {
    o3: [
        { code: 'SAVE5', uses: 142, remaining: 358, revenue: 5524.30, lastUsed: '2026-05-22T11:30:00Z' },
        { code: 'TAKEOUT5', uses: 45, remaining: null, revenue: 1750.40, lastUsed: '2026-05-21T19:15:00Z' },
    ],
    o6: [
        { code: 'STUDENT15', uses: 95, remaining: null, revenue: 2736.00, lastUsed: '2026-05-22T13:00:00Z' },
    ],
    o7: [
        { code: 'WELCOME10', uses: 73, remaining: 127, revenue: 3248.50, lastUsed: '2026-05-20T20:00:00Z' },
    ],
};

// Null-safe: some offer-detail fields arrive null when the backend can't compute
// metrics (see `metricsAvailable`). Formatting null/undefined/NaN directly used to
// throw (`null.toLocaleString()`), which — with no app-level error boundary —
// blanked the whole report. Fall back to an em-dash instead.
export const fmtNum = (n: number | null | undefined) =>
    n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString();
// Money prefix is the outlet's own symbol (IN → ₹, US → $, elsewhere → none),
// resolved at call time like SnapshotScreen's fmtCurrency — never a hardcoded '$'.
export const fmtMoney = (n: number | null | undefined) =>
    n == null || Number.isNaN(Number(n)) ? '—' : `${getCurrencySymbol()}${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const truncate = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

export const TYPE_COLORS: Record<OfferType, string> = {
    '% Off': T.accent,
    '$ Off': T.green,
    'BOGO': T.orange,
    'Free Item': '#6366F1',
    'Free Delivery': '#06B6D4',
};

export const TYPE_BG: Record<OfferType, string> = {
    '% Off': T.accentSoft,
    '$ Off': T.greenSoft,
    'BOGO': T.orangeSoft,
    'Free Item': 'rgba(99,102,241,0.10)',
    'Free Delivery': 'rgba(6,182,212,0.10)',
};

export const STATUS_STYLE: Record<OfferStatus, { bg: string; fg: string }> = {
    Active: { bg: T.greenSoft, fg: T.green },
    Paused: { bg: T.orangeSoft, fg: T.orange },
    Expired: { bg: T.hover, fg: T.t3 },
    Draft: { bg: T.hover, fg: T.t2 },
};

export function TypeBadge({ type }: { type: OfferType }) {
    return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: TYPE_BG[type], color: TYPE_COLORS[type] }}>{type}</span>;
}

export function StatusPill({ status }: { status: OfferStatus }) {
    const s = STATUS_STYLE[status];
    return <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 10, fontWeight: 600, background: s.bg, color: s.fg }}>{status}</span>;
}

// ── API enum → UI mappers ────────────────────────────────────────────────────
// The offers endpoints return SCREAMING_SNAKE enums (PERCENTAGE, FREE_ITEM,
// PAUSED …). These are rendered VERBATIM — the map below resolves the badge
// colour only, never the label, so an unknown enum shows its own value in a
// neutral badge instead of being silently relabelled.
const API_OFFER_TYPE_LABEL: Record<string, OfferType> = {
    PERCENTAGE: '% Off',
    FLAT: '$ Off',
    BOGO: 'BOGO',
    FREE_ITEM: 'Free Item',
    FREE_DELIVERY: 'Free Delivery',
};

// Palette key for an API offer type. The endpoints may send the SCREAMING_SNAKE
// enum (FLAT) or the display label itself ("$ Off"), so both are resolved — and
// always from the RAW value, before any currency rewrite ("₹ Off" is not a key).
const apiTypeKey = (apiType: string): OfferType | undefined =>
    API_OFFER_TYPE_LABEL[apiType]
    ?? (Object.prototype.hasOwnProperty.call(TYPE_COLORS, apiType) ? (apiType as OfferType) : undefined);

// Colour for an API offer type, matching the badge palette. Unknown types fall
// back to a muted token so the donut still renders a slice.
export const apiTypeColor = (apiType: string): string => {
    const mapped = apiTypeKey(apiType);
    return mapped ? TYPE_COLORS[mapped] : T.t3;
};

// The label is the API's own value, with only the currency symbol localised to
// the outlet's country ("$ Off" → "₹ Off" outside the US) — never relabelled.
export function ApiTypeBadge({ type }: { type: string }) {
    useCurrencyRevision();
    const mapped = apiTypeKey(type);
    const bg = mapped ? TYPE_BG[mapped] : T.hover;
    const color = mapped ? TYPE_COLORS[mapped] : T.t2;
    return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: bg, color }}>{localizeCurrencyText(type)}</span>;
}

// Status pill for an API status enum — the enum is the label; only the colour is
// looked up (case-insensitively), with a neutral fallback for unknown values.
const NEUTRAL_STATUS_STYLE = { bg: T.hover, fg: T.t2 };

export function ApiStatusPill({ status }: { status: string }) {
    const key = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    const s = STATUS_STYLE[key as OfferStatus] ?? NEUTRAL_STATUS_STYLE;
    return <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 10, fontWeight: 600, background: s.bg, color: s.fg }}>{status}</span>;
}

// percentage may arrive as a number (10) or a "10%" string. Parsed for geometry
// (bar widths) only — displayed percentages must use fmtPct so the backend's own
// precision survives.
export const parsePct = (v: number | string | null | undefined): number => {
    if (typeof v === 'number') return isFinite(v) ? v : 0;
    if (typeof v === 'string') { const n = parseFloat(v); return isNaN(n) ? 0 : n; }
    return 0;
};

// Display a backend percentage exactly as sent (89.98 → "89.98%") — never
// rounded or re-derived from the underlying amounts.
export const fmtPct = (v: number | string | null | undefined): string => `${parsePct(v)}%`;

// Recharts XAxis `interval` for a daily-date axis, sized from the number of points
// the API actually returned rather than the selected preset — a range can come back
// short (a new outlet, a partial month). `interval` counts SKIPPED ticks, so a label
// every N days is interval = N - 1.
//   ≤ 8 pts  → every day      (interval 0)
//   ≤ 31 pts → every 5 days   (interval 4)
//   ≤ 91 pts → every 15 days  (interval 14)
//   beyond   → ~6 labels, whatever the length
//
// Each band carries a +1 tolerance over its nominal preset length because the
// backend appends the IN-PROGRESS CURRENT DAY past the requested `endDate`: a 7D
// preset asks for 20-Jul→26-Jul and gets 8 rows back, 20-Jul…27-Jul. Without the
// tolerance a 7-day range fell into the ≤30 band and drew a label every 5 days —
// only the 1st and 6th dates (17-Jul, 22-Jul), which is unreadable as a daily
// trend. 30D / This Month were hit the same way (31 pts → every 15 days, 3 labels).
// The extra day is NOT trimmed from the series: the API counts it in
// `totalRedemptions`, so dropping it would desync the chart from the KPI tile.
export const dateTickInterval = (pointCount: number): number => {
    if (pointCount <= 8) return 0;
    if (pointCount <= 31) return 4;
    if (pointCount <= 91) return 14;
    return Math.max(1, Math.ceil(pointCount / 6)) - 1;
};

// Layout for a daily-date x-axis, shared by all three trend charts (the Offers
// Report's Redemption Trend card and the Redemption Trend / Individual Offer
// drills) so they stay visually identical.
//   PADDING       — keeps the first point off the y-axis line instead of flush
//                   against it, with matching room at the far end.
export const DATE_AXIS_PADDING = { left: 10, right: 10 } as const;

// Tick geometry for the daily-date axis (ZAR-1471 ⑤).
//
// Horizontal DD-Mon labels don't fit a phone-width card: "22-Jul" is ~34px at
// 9px type, and a 7D range labels every day (`dateTickInterval` → 0) inside a
// plot area of roughly 280px, i.e. ~35px per label. Consecutive labels touch and
// the axis reads as one smear ("22-Jul23-Jul24-Jul…").
//
// Below desktop the labels are therefore rotated -45° and right-anchored, the
// same treatment EventReport's "Pickup by slot" chart uses, which decouples the
// label width from the per-tick spacing. Rotated text needs the axis band to be
// taller than the default 30px or the SVG clips the descending tails, hence the
// explicit `height`. Desktop keeps them horizontal — the card is wide enough
// there that they never collide.
export const dateAxisTickProps = (isDesktop: boolean) =>
    isDesktop ? {} : { angle: -45, textAnchor: 'end' as const, height: 46, tickMargin: 2 };

// Right margin for the chart wrapping a daily-date axis. Recharts centres a
// horizontal tick label on its data point, so the last label needs half its
// width outside the plot area or the SVG clips it ("27-Jul" → "27-Ju"); 28px
// covers the widest label `makeDateTickFormatter` emits, including the
// multi-year "27-Jul-25" form. Rotated labels are right-anchored and hang to the
// *left* of their tick instead, so that overhang isn't needed — reclaim the
// space as plot width, which is scarcest exactly where the labels rotate.
export const dateAxisRightMargin = (isDesktop: boolean) => (isDesktop ? 28 : 10);

// Extra chart height to pay for the taller rotated axis band, so rotating the
// labels doesn't flatten the trend line into the space the ticks took.
export const dateAxisExtraHeight = (isDesktop: boolean) => (isDesktop ? 0 : 30);

// Abbreviated month names for the DD-Mon axis labels (ZAR-1471 ①).
const MONTHS_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Format an ISO date (YYYY-MM-DD) as DD-Mon, e.g. "2026-04-23" → "23-Apr". When
// `withYear`, a 2-digit year is appended ("23-Apr-25") to disambiguate a range
// that spans more than one calendar year. Falls back to the raw month-day slice
// if the input isn't a well-formed ISO date.
export const fmtDdMon = (iso: string, withYear = false): string => {
    const parts = String(iso).split('-');
    if (parts.length < 3) return String(iso).slice(5);
    const mon = MONTHS_ABBR[Number(parts[1]) - 1];
    if (!mon) return String(iso).slice(5);
    const dd = parts[2].padStart(2, '0');
    return withYear ? `${dd}-${mon}-${parts[0].slice(2)}` : `${dd}-${mon}`;
};

// Tick formatter for a daily-date axis, built from the dates actually returned.
// Within a single year "2026-04-23" → "23-Apr"; once the range spans more than
// one year that would be ambiguous, so the 2-digit year is carried: "23-Apr-25".
export const makeDateTickFormatter = (dates: string[]) => {
    const multiYear = new Set(dates.map((d) => d.slice(0, 4))).size > 1;
    return (d: string) => fmtDdMon(d, multiYear);
};
